import { Injectable } from "@angular/core";
import { Storage } from "@ionic/storage-angular";
import { DownloadedClient } from "../types/Client/DownloadedClient";
import { AuthCredentials } from "../types/Auth/Auth";

interface DownloadedClientsMeta {
  ids: string[];
  currentClientID?: string;
}

@Injectable({
  providedIn: "root",
})
export class DownloadedClientRepository {
  private readonly clientKeyPrefix = "downloadedClient";

  private readonly metaKey = "downloadedClientsMeta";

  private readonly legacyArrayKey = "downloadedClients";

  private readonly legacyCurrentKey = "currentClient";

  private migrationPromise: Promise<void> | null = null;

  constructor(private storage: Storage) {}

  private getClientStorageKey(dlClientID: string): string {
    return `${this.clientKeyPrefix}:${dlClientID}`;
  }

  private ensureMigrated(): Promise<void> {
    if (!this.migrationPromise) {
      this.migrationPromise = this.runLegacyMigration();
    }
    return this.migrationPromise;
  }

  private async runLegacyMigration(): Promise<void> {
    const meta = await this.getMetaInternal();
    const legacyClients = (await this.storage.get(
      this.legacyArrayKey,
    )) as DownloadedClient[];
    if (Array.isArray(legacyClients) && legacyClients.length) {
      await Promise.all(
        legacyClients.map((client) =>
          this.persistClientRecordInternal(client, meta),
        ),
      );
      await this.storage.remove(this.legacyArrayKey);
    }
    const legacyCurrent = (await this.storage.get(
      this.legacyCurrentKey,
    )) as DownloadedClient;
    if (legacyCurrent?.dlClientID) {
      await this.persistClientRecordInternal(legacyCurrent, meta);
      meta.currentClientID = legacyCurrent.dlClientID;
      await this.storage.remove(this.legacyCurrentKey);
    }
    await this.setMetaInternal(meta);
  }

  private async getMetaInternal(): Promise<DownloadedClientsMeta> {
    const meta = ((await this.storage.get(this.metaKey)) || {
      ids: [],
    }) as DownloadedClientsMeta;
    if (!meta.ids) {
      meta.ids = [];
    }
    return meta;
  }

  private async setMetaInternal(meta: DownloadedClientsMeta): Promise<void> {
    await this.storage.set(this.metaKey, meta);
  }

  private async persistClientRecordInternal(
    client: DownloadedClient,
    meta: DownloadedClientsMeta,
  ): Promise<void> {
    const key = this.getClientStorageKey(client.dlClientID);
    await this.storage.set(key, client);
    if (!meta.ids.includes(client.dlClientID)) {
      meta.ids.push(client.dlClientID);
    }
  }

  public async get(): Promise<DownloadedClient[]> {
    await this.ensureMigrated();
    const meta = await this.getMetaInternal();
    const clients = await Promise.all(
      meta.ids.map((id) => this.storage.get(this.getClientStorageKey(id))),
    );
    return clients.filter(Boolean) as DownloadedClient[];
  }

  public async getById(
    dlClientID: string,
  ): Promise<DownloadedClient | undefined> {
    await this.ensureMigrated();
    return this.storage.get(this.getClientStorageKey(dlClientID));
  }

  public async getClientsByAuth(
    auth: AuthCredentials,
  ): Promise<DownloadedClient[]> {
    const clients = await this.get();
    return clients.filter(
      (client) => client.userID === auth.userID && client.env === auth.env,
    );
  }

  public async saveClient(client: DownloadedClient): Promise<DownloadedClient> {
    await this.ensureMigrated();
    const meta = await this.getMetaInternal();
    await this.persistClientRecordInternal(client, meta);
    await this.setMetaInternal(meta);
    return client;
  }

  public async removeClient(dlClientID: string): Promise<void> {
    await this.ensureMigrated();
    const meta = await this.getMetaInternal();
    meta.ids = meta.ids.filter((id) => id !== dlClientID);
    if (meta.currentClientID === dlClientID) {
      delete meta.currentClientID;
    }
    await Promise.all([
      this.storage.remove(this.getClientStorageKey(dlClientID)),
      this.setMetaInternal(meta),
    ]);
  }

  public async getCurrentClientId(): Promise<string | undefined> {
    await this.ensureMigrated();
    const meta = await this.getMetaInternal();
    return meta.currentClientID;
  }

  public async setCurrentClientId(
    dlClientID: string | undefined,
  ): Promise<void> {
    await this.ensureMigrated();
    const meta = await this.getMetaInternal();
    meta.currentClientID = dlClientID || undefined;
    await this.setMetaInternal(meta);
  }

  public async getCurrentClient(): Promise<DownloadedClient | undefined> {
    const currentClientID = await this.getCurrentClientId();
    if (!currentClientID) {
      return undefined;
    }
    return this.getById(currentClientID);
  }

  public async clear(): Promise<void> {
    await this.ensureMigrated();
    const meta = await this.getMetaInternal();
    const removal = meta.ids.map((id) =>
      this.storage.remove(this.getClientStorageKey(id)),
    );
    await Promise.all(removal);
    await this.storage.remove(this.metaKey);
  }
}
